import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useForm } from 'react-hook-form';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { Card, ProgressSpinner, InputText, FloatLabel, SCButton, Controller, DDCountries, DDStakeholderType, TabPanel, TabView } from '../../Common/Imports';
import { ApiResponse, Stakeholder_EXT } from '../../Models/Classes';
import ConfigServiceList from '../Config_Service/ConfigService_List';
import ConfigMailTemplatesList from '../Config_MailTemplates/ConfigMailTemplates_List';
import ConfigStatusList from '../Config_Status/ConfigStatus_List';
import UserList from '../Users/Users_List';
import BrandList from '../Brands/Brands_List';
import ContactsList from '../Contacts/Contacts_List';
import { FormatDateTimeFromDate } from '../../Common/Utility/FormatDateTimeFromDate';

interface StakeholderPopupProps {
    stakeholderId: number;
}

export const StakeholderDetail: React.FC<StakeholderPopupProps> = ({ stakeholderId }) => {
    const { t } = useTranslation();
    const AxiosHelper = useAxios();
    const { control, handleSubmit, setValue } = useForm<Stakeholder_EXT>();
    const [loading, setLoading] = useState<boolean>(true);
    const { showToast } = useToast();
    const [data, setData] = useState<Stakeholder_EXT>();

    const GetData = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<Stakeholder_EXT>>(`Stakeholder/GetByIdStakeholder/${stakeholderId}`);
            if (resp.data.code === 0) {
                const stakeholderData = resp.data.data;
                Object.keys(stakeholderData).forEach((key) => {
                    setValue(key as keyof Stakeholder_EXT, stakeholderData[key as keyof Stakeholder_EXT]);
                });
                stakeholderData.updated = stakeholderData.updated ? new Date(stakeholderData.updated) : undefined;
                stakeholderData.created = stakeholderData.created ? new Date(stakeholderData.created) : undefined;
                setData(stakeholderData);
            }
        } catch (error) {
            console.error("Error fetching stakeholder data:", error);
        } finally {
            setLoading(false);
        }
    }, [AxiosHelper, stakeholderId, setValue]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const updateStakeholder = async (data: Stakeholder_EXT) => {
        setLoading(true);
        try {
            const resp = await AxiosHelper.post<ApiResponse<Stakeholder_EXT>>(`Stakeholder/UpdateStakeholder`, data);
            if (resp.data.code === 0) {
                showToast('success', 'Success', t("Common.Toasts.Update.Success"));
                GetData();
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.Update.Failure"));
        } finally {
            setLoading(false);
        }
    };

    const headerTemplate = () => (
        <div className="col d-flex justify-content-end">
            <SCButton Name="Save" Label={t("Common.Buttons.Save")} OnClick={handleSubmit(updateStakeholder)} />
        </div>
    );

    const footerTemplate = () => (
        data &&
        <div className="row">
            <div className="col d-flex justify-content-start">
                <>{t("Stakeholder.Edit.Created")} {data && FormatDateTimeFromDate(data.created, { withTime: true })}</>
            </div>
            <div className="col d-flex justify-content-end">
                <>{t("Stakeholder.Edit.Updated")} {data && FormatDateTimeFromDate(data.updated, { withTime: true })}</>
            </div>
        </div>
    );

    return (
        loading ? (
            <ProgressSpinner />
        ) : (
            <>
                <Card className={data?.isActive ? "mb-3 border border-success" : "mb-3 border border-danger"} footer={footerTemplate} header={headerTemplate}>
                    <form className="p-fluid" onSubmit={handleSubmit(updateStakeholder)}>
                        <div className="row mb-4">
                            <div className="col-md-6 mb-4">
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field }) => (
                                        <FloatLabel>
                                            <InputText id="name" {...field} />
                                            <label htmlFor="name">{t("Stakeholder.Edit.Name")}</label>
                                        </FloatLabel>
                                    )}
                                />
                            </div>
                            <div className="col-md-6 mb-4">
                                <Controller
                                    name="address"
                                    control={control}
                                    render={({ field }) => (
                                        <FloatLabel>
                                            <InputText id="address" {...field} />
                                            <label htmlFor="address">{t("Stakeholder.Edit.Address")}</label>
                                        </FloatLabel>
                                    )}
                                />
                            </div>
                            <div className="col-md-3 mb-4">
                                <Controller
                                    name="city"
                                    control={control}
                                    render={({ field }) => (
                                        <FloatLabel>
                                            <InputText id="city" {...field} />
                                            <label htmlFor="city">{t("Stakeholder.Edit.City")}</label>
                                        </FloatLabel>
                                    )}
                                />
                            </div>
                            <div className="col-md-3 mb-4">
                                <Controller
                                    name="region"
                                    control={control}
                                    render={({ field }) => (
                                        <FloatLabel>
                                            <InputText id="region" {...field} />
                                            <label htmlFor="region">{t("Stakeholder.Edit.Region")}</label>
                                        </FloatLabel>
                                    )}
                                />
                            </div>
                            <Controller
                                name="idCountry"
                                control={control}
                                rules={{ required: `${t("Stakeholder.Edit.Country")} ${t("Common.Form.Required")}` }}
                                render={({ field, fieldState }) => (
                                    <div className="col-md-3 mb-4">
                                        <FloatLabel>
                                            <DDCountries
                                                value={field.value}
                                                onChange={(value) => field.onChange(value)}
                                                onBlur={field.onBlur}
                                                valid={fieldState.invalid}
                                            />
                                            <label htmlFor="zip">{t("Stakeholder.Edit.Country")}</label>
                                        </FloatLabel>
                                        {fieldState.invalid && (
                                            <small className="p-error">
                                                {fieldState.error?.message}
                                            </small>
                                        )}
                                    </div>
                                )}
                            />
                            <div className="col-md-3 mb-4">
                                <Controller
                                    name="zip"
                                    control={control}
                                    render={({ field }) => (
                                        <FloatLabel>
                                            <InputText id="zip" {...field} />
                                            <label htmlFor="zip">{t("Stakeholder.Edit.ZIP")}</label>
                                        </FloatLabel>
                                    )}
                                />
                            </div>
                            <div className="col-md-3">
                                <Controller
                                    name="vat"
                                    control={control}
                                    render={({ field }) => (
                                        <FloatLabel>
                                            <InputText id="vat" {...field} />
                                            <label htmlFor="vat">{t("Stakeholder.Edit.VAT")}</label>
                                        </FloatLabel>
                                    )}
                                />
                            </div>
                            <div className="col-md-3">
                                <Controller
                                    name="stakeholderType"
                                    control={control}
                                    render={({ field }) => (
                                        <FloatLabel>
                                            <DDStakeholderType
                                                value={field.value}
                                                onSelect={(e) => field.onChange(e)}
                                            />
                                            <label htmlFor="stakeholderType">{t("Stakeholder.Edit.Type")}</label>
                                        </FloatLabel>
                                    )}
                                />
                            </div>
                            <div className="col-md-3">
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field }) => (
                                        <FloatLabel>
                                            <InputText id="email" {...field} />
                                            <label htmlFor="email">{t("Stakeholder.Edit.Email")}</label>
                                        </FloatLabel>
                                    )}
                                />
                            </div>
                            <div className="col-md-3">
                                <Controller
                                    name="phone"
                                    control={control}
                                    render={({ field }) => (
                                        <FloatLabel>
                                            <InputText id="phone" {...field} />
                                            <label htmlFor="vphoneat">{t("Stakeholder.Edit.Phone")}</label>
                                        </FloatLabel>
                                    )}
                                />
                            </div>
                        </div>
                    </form>
                </Card>
                <Card className={data?.isActive ? "mb-3 border border-success" : "mb-3 border border-danger"}>
                    <TabView>
                        <TabPanel header={t("Stakeholder.Edit.Tabs.Users")}>
                            <UserList stakeholderId={stakeholderId} />
                        </TabPanel>
                        <TabPanel header={t("Stakeholder.Edit.Tabs.Brands")}>
                            <BrandList stakeholderId={stakeholderId} />
                        </TabPanel>
                        <TabPanel header={t("Stakeholder.Edit.Tabs.ServiceConfig")}>
                            <ConfigServiceList stakeholderId={stakeholderId} />
                        </TabPanel>
                        <TabPanel header={t("Stakeholder.Edit.Tabs.TemplateConfig")}>
                            <ConfigMailTemplatesList stakeholderId={stakeholderId} />
                        </TabPanel>
                        <TabPanel header={t("Stakeholder.Edit.Tabs.StatusConfig")}>
                            <ConfigStatusList stakeholderId={stakeholderId} />
                        </TabPanel>
                        <TabPanel header={t("Stakeholder.Edit.Tabs.Contacts")}>
                            <ContactsList stakeholderId={stakeholderId} />
                        </TabPanel>
                    </TabView>
                </Card>
            </>
        )
    );
};

export default StakeholderDetail;
